import React, { useMemo, useRef, useState } from 'react';
import { Confirm, Title, useNotify } from 'react-admin';
import { Box, Chip, LinearProgress, Tooltip } from '@mui/material';
import AgGrid from '../../components/ag-grid/ag-grid';
import { ActionColumn } from '../../components/ag-grid/aggrid-components';
import { dataProvider } from '../../DataProvider';
import { dayjs, toLocalTime } from '../../components/ex_dayjs';
import { ExchangeMarket, queueStatusEnum } from '../../utils/enum';
import { loadSourceType, reloadPage } from '../../utils/common';
import { AgGridHandle } from '../../components/ag-grid/share/type';
import RenderControls from '../../components/ra-list/RenderControls';
import { FilterListProvider } from '../../context/FilterListProvider';
import './css/style.css';
import MappingPopup from '../listings-mapping/components/MappingPopup';
import BulkActionButtonBar from './components/bulk-action-button-bar';
import BulkStatusDialog from './components/BulkStatusDialog';
import RejectedDescriptionDialog from './components/RejectedDescriptionDialog';
import QueueUploadDescription from './components/QueueUploadDescription';


//#region Types
interface RecordData {
    siteId: string;
    eventId: number;
    marketId: number;
    type: string;
    name: string;
    venue: string;
    eventDate: string;
    status: number;
    description?: string;
    insertedOn: string;
    insertedBy: string;
    updatedOn: string;
    updatedBy: string;
    lastModified: string;
    [key: string]: any;
}

interface ChangeData {
    newValue: any;
    oldValue: any;
}
//#endregion

const QueueUploadView: React.FC = () => {
    const notify = useNotify();
    const refInput = useRef<AgGridHandle>(null);
    const refFilter = useRef(null);

    const [marketType, setMarketType] = useState<{ id: string; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<RecordData[]>([]);
    const [descriptions, setDescriptions] = useState<string[]>([]);

    // Rejected-description popup state — single-row and bulk flows share this dialog.
    const [rejectedDialog, setRejectedDialog] = useState<{
        open: boolean;
        // Callback to invoke once the user confirms a description; receives the resolved description string.
        pendingAction: null | ((desc: string) => void);
        // Only set for single-row edits: reverts the AgGrid cell to the original status on cancel.
        rollbackAction: null | (() => void);
            }>({ open: false, pendingAction: null, rollbackAction: null });

    const [openConfirm, setOpenConfirm] = useState({
        mode: '',
        open: false,
        data: {} as RecordData,
        changedData: {} as ChangeData
    });

    // bulkStatusDialog only tracks open state; the chosen status is owned by BulkStatusDialog internally.
    const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
    const [bulkPendingStatus, setBulkPendingStatus] = useState<number | null>(null);

    const [defaultValuesAgGrid, setDefaultValuesAgGrid] = useState({
        sortField: { field: 'lastModified', order: 'DESC' }
    });
    const [defaultValues] = useState({
        marketId_equal: 1,
        status_equal: 0,
        lastModified: [dayjs().add(-3, 'day').format('YYYY-MM-DD'), dayjs().endOf('day').format('YYYY-MM-DD')],
        score: [0, 5],
    });
    //#region for mapping dialogs
    const [state, setState] = React.useState({
        m_isOpen: false,
        m_isLoading: false,
        m_item: {},
        m_events: [],
        // for sync dialog
        s_isOpen: false,
        s_title: 'DETAIL',
        s_data: {},
        // for unmap all
        unmap_isLoading: false,
        unmap_isOpen: false,
        unmap_alert: false,
        unmap_sync_isLoading: false,
        // for skip pricer
        skipBtn: 'skip pricer',
        smEventId: '', // For sync button,
        //
        selectMarkektId: null,
    });
    const handleCloseDialog = (event: any, dialogName: string | null = null, action: string | null = null) => {
        let name = event?.currentTarget?.ariaLabel;
        if (!name) name = dialogName;
        if (name) setState((prev) => ({ ...prev, [name]: false }));
        if (!!action) reloadPage('QueueUpload', refFilter);
    };
    //#endregion

    React.useEffect(() => {
        loadSourceType(dataProvider, (response: any) => setMarketType(response));
        // Load rejection descriptions from the backend (category = "QueueUpload")
        dataProvider.fetchData('QueueUpload', 'GetDescriptions')
            .then((res: any) => {
                if (res?.data?.descriptions) {
                    setDescriptions(Object.keys(res.data.descriptions).sort());
                }
            })
            .catch(() => {/* non-critical; user can still open QueueUploadDescription to add entries */});
    }, []);

    //#region Filter controls definition (mirrors event-delivery-view.js pattern)
    const controls = [
        { component: 'select', name: 'type_equal', label: 'Market', choices: marketType },
        { component: 'select', name: 'marketId_equal', label: 'Exchange', choices: ExchangeMarket, isEmptyOption: false },
        { component: 'select', name: 'status_equal', label: 'Status', choices: queueStatusEnum },
        { component: 'number', name: 'eventId_equal', label: 'Event ID', xs: 1 },
        { component: 'text', name: 'siteId_contains', label: 'Site ID', xs: 1 },
        { component: 'text', name: 'name_contains', label: 'Event Name', xs: 1.5 },
        { component: 'text', name: 'venue_contains', label: 'Venue Name', xs: 1.5 },
        { component: 'daterange', name: 'lastModified', label: 'Updated From', isUTC: true },
        { component: 'daterange', name: 'insertedOn', label: 'Inserted From', isUTC: true },
        { component: 'text', name: 'updatedBy_contains', label: 'Updated By', xs: 1.5 },
        { component: 'text', name: 'insertedBy_contains', label: 'Inserted By', xs: 1.5 },
        { component: 'text', name: 'affected_By_Rule_IDs_contains', label: 'Rule IDs', xs: 0.75 },
        { component: 'numberrange', name: 'score', label: 'Score', xs: 1, decimal: 3, rangeMin: [0, 5], rangeMax: [0, 5] },
        { component: 'checkbox', name: 'Last24', label: 'Last 24h', xs: 0.8 },
        { component: 'checkbox', name: 'SkipTag', label: 'Skip tags', xs: 0.8 },
    ];
    //#endregion

    const handleSearch = (filterList: Record<string, any>) => {
        refInput.current?.customFilterSearch(filterList);
    };

    //#region AgGrid column definitions
    const getStatusColor = (statusId: number) => {
        if (statusId === undefined || statusId === null) {
            return 'info';
        }
        switch (statusId) {
            case 0:  return 'info';
            case 1:  return 'warning';
            case 2:  return 'success';
            case 3:  return 'error';
            case 4:  return 'secondary';
            case -1: return 'default';
            default: return 'default';
        }
    };

    /**
     * Shows Description (if available) in a tooltip when hovering over the Status chip, if a description exists for the record.
     * @param params
     * @returns JSX.Element rendering the Status cell with a colored chip and optional tooltip, or null if status is not defined.
     */
    const renderStatusChip = (params: any): JSX.Element | null => {
        if (!params.data || params.data.status === undefined || params.data.status === null) {
            return null;
        }

        const description = params.data.description;
        const chipLabel = queueStatusEnum.find(item => item.id === params.value)?.name || params.value;

        const chipComponent = (
            <Chip
                label={chipLabel}
                size="small"
                color={getStatusColor(params.data.status) as any}
            />
        );

        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                {description ? (
                    <Tooltip title={description} arrow placement="top">
                        <span>{chipComponent}</span>
                    </Tooltip>
                ) : (
                    chipComponent
                )}
            </Box>
        );
    };

    const colDefs = [
        {
            field: 'checkboxSelection',
            maxWidth: 30,
            checkboxSelection: true,
            headerCheckboxSelection: true,
        },
        {
            field: 'siteId', headerName: 'SiteID', minWidth: 70, maxWidth: 150,
            cellClass: 'myag-cell-wrap-text',
            
        },
        {
            field: 'marketId', headerName: 'Exchange', maxWidth: 90,
            valueFormatter: (params: any) => ExchangeMarket.find(item => item.id === params.value)?.name || params.value
        },
        {
            field: 'type', headerName: 'Market Type', maxWidth: 85, headerClass: 'myag-header-cell-wrap-text',
            cellRenderer: (params: any) => params.data.originalUrl
                ? <a target="_blank" href={params.data.originalUrl} rel="noreferrer"
                    style={{ color: '#f59e0b', textDecoration: 'none' }}>
                    {params.value}
                </a>
                : <span>{params.value}</span>
        },
        {
            field: 'name', headerName: 'Name', minWidth: 300, cellClass: 'myag-cell-wrap-text',
            cellRenderer: (params: any) => params.data.smUrl
                ? <a target="_blank" href={params.data.smUrl} rel="noreferrer"
                    style={{ color: '#3b82f6', textDecoration: 'none' }}>
                    {params.value}
                </a>
                : <span>{params.value}</span>
        },
        { field: 'venue', headerName: 'Venue', minWidth: 200, cellClass: 'myag-cell-wrap-text' },
        {
            field: 'eventDate', headerName: 'Event Date', maxWidth: 90,
            cellRenderer: (params: any) => {
                const eventDate = params.value ? params.value.replace(/z/i, '') : null;
                return eventDate ? (
                    <>
                        {dayjs(eventDate).format('YYYY-MM-DD')}
                        <br />
                        {dayjs(eventDate).format('hh:mm A')}
                    </>
                ) : '-';
            }
        },
        {
            field: 'status', headerName: 'Status', minWidth: 100, maxWidth: 150,
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
                values: queueStatusEnum.map(item => item.id),
                formatValue: (params: any) => {
                    let mapped = params.data.mapped === 1;
                    let paramValue = (mapped || params.value === 2) ? 2 : params.value; // if mapped show as verified, otherwise show as original status
                    return queueStatusEnum.find(item => item.id === paramValue)?.name || paramValue;
                }
            },
            valueParser: (params: any) => {
                const selected = queueStatusEnum.find(item => item.name === params.newValue);
                return selected ? selected.id : params.newValue;
            },
            valueFormatter: (params: any) => queueStatusEnum.find(item => item.id === params.value)?.name || params.value,
            cellRenderer: renderStatusChip
        },
        {
            field: 'insertedOn', headerName: 'Inserted On', maxWidth: 90,
            cellRenderer: (params: any) => {
                const eventDate = params.value ? params.value.replace(/z/i, '') : null;
                return eventDate ? (
                    <>
                        {toLocalTime(eventDate).format('YYYY-MM-DD')}
                        <br />
                        {toLocalTime(eventDate).format('hh:mm A')}
                    </>
                ) : '-';
            }
        },
        { field: 'insertedBy', headerName: 'Inserted By', maxWidth: 120, cellClass: 'myag-cell-wrap-text' },
        {
            field: 'lastModified', headerName: 'Last Modified', maxWidth: 90, headerClass: 'myag-header-cell-wrap-text',
            cellRenderer: (params: any) => {
                const finalDate = params.value;
                const eventDate = finalDate ? finalDate.replace(/z/i, '') : null;
                return eventDate ? (
                    <>
                        {toLocalTime(eventDate).format('YYYY-MM-DD')}
                        <br />
                        {toLocalTime(eventDate).format('hh:mm A')}
                    </>
                ) : '-';
            }
        },
        { field: 'updatedBy', headerName: 'Updated By', maxWidth: 120, cellClass: 'myag-cell-wrap-text',
            valueFormatter: (params: any) => params.data.mappedBy || params.value,
        },
        { field: 'score', headerName: 'Score', maxWidth: 80 },
        { field: 'attemptCount', headerName: 'Attempt Count', maxWidth: 80, headerClass: 'myag-header-cell-wrap-text',
            valueFormatter: (params: any) => params.value !== undefined && params.value !== null ? params.value : 1,
        },
        {
            field: 'tags', headerName: 'Tags', minWidth: 120,
            tooltipValueGetter: (params: any) => 'Tagged by: ' + params.data.tag_UpdatedBy,
            cellRenderer: (params: any) => {
                if (!params.value) return null;
                const tags = (params.value as string).split(',').filter((t: string) => t !== '');
                if (tags.length === 0) return null;
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', height: '100%' }}>
                        {tags.map((tag: string) => (
                            <Chip key={tag} label={tag} size="small" />
                        ))}
                    </Box>
                );
            }
        },
        { field: 'affected_By_Rule_IDs', headerName: 'RuleIDs', minWidth: 100,
            valueFormatter: (params: any) => params.value ? params.value.split(',').filter(Boolean).join(', ') : '',
        },
        { headerName: 'Action', minWidth: 150, cellRenderer: ActionColumn, colId: 'action' }
    ];

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        flex: 1,
        filter: false,
        suppressMovable: true
    }), []);
    //#endregion

    //#region Edit / Delete handlers
    const handleDelete = (eventId: number, marketId: number) => {
        setLoading(true);
        dataProvider.postData('QueueUpload', `DeleteEvent?eventId=${eventId}&marketId=${marketId}`, null, 'DELETE')
            .then((res: any) => {
                if (res?.data?.success) {
                    notify(`${res.data.count} Event deleted successfully`, { type: 'success' });
                    reloadPage('QueueUpload', refFilter);
                } else {
                    notify('Delete failed', { type: 'error' });
                }
            })
            .catch((err: any) => {
                notify(`Something went wrong: ${err}`, { type: 'error', autoHideDuration: 2000 });
            })
            .finally(() => {
                setOpenConfirm({ mode: '', open: false, data: {} as RecordData, changedData: {} as ChangeData });
                setLoading(false);
            });
    };

    const handleEdit = (eventId: number, marketId: number, _data: RecordData, changedData: ChangeData, description?: string) => {
        setLoading(true);
        dataProvider.postData('QueueUpload', 'UpsertQueueUpload', [{ eventId, marketId, siteId: _data.siteId, status: changedData.newValue, description: description ?? null }])
            .then((res: any) => {
                if (res?.data?.success) {
                    notify(res.data.message, { type: 'success' });
                    const api = refInput.current?.getAgGridInstance()?.api;
                    if (api) {
                        api.forEachNode((node: any) => {
                            if (!node.data) return;
                            const isUpdate = node.data.eventId === eventId && node.data.marketId === marketId;
                            if (isUpdate) {
                                node.setDataValue('lastModified', new Date().toISOString());
                                node.setDataValue('updatedBy', res.data.updatedBy);
                                if (changedData.newValue === 0) { // if change to new, increase attempt count by 1
                                    const currentCount = node.data.attemptCount !== undefined && node.data.attemptCount !== null ? node.data.attemptCount : 1;
                                    node.setDataValue('attemptCount', currentCount + 1);
                                }
                            }
                        });
                    }
                } else {
                    notify('Update failed', { type: 'error' });
                }
            })
            .catch((err: any) => {
                notify(`Something went wrong: ${err}`, { type: 'error', autoHideDuration: 2000 });
            })
            .finally(() => {
                setOpenConfirm({ mode: '', open: false, data: {} as RecordData, changedData: {} as ChangeData });
                setLoading(false);
            });
    };

    const onCellClicked = (params: any) => {
        if (params.column.colId === 'action' && params.event.target.dataset.action) {
            const action = params.event.target.dataset.action;
            if (action === 'edit') {
                params.api.startEditingCell({
                    rowIndex: params.node.rowIndex,
                    colKey: params.columnApi.getDisplayedCenterColumns()[0].colId
                });
            }
            if (action === 'delete') {
                setOpenConfirm(prev => ({ ...prev, mode: 'delete', open: true, data: params.data }));
            }
            if (action === 'update') {
                params.api.stopEditing(false);
                params.api.stopEditing();
            }
            if (action === 'cancel') {
                params.api.stopEditing(true);
            }
        }
    };

    const onCellEditingStopped = (params: any) => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
        if (params.valueChanged) {
            const changedData = { newValue: params.newValue, oldValue: params.oldValue };

            // Rejected status requires a description — open description popup before persisting.
            // Store a rollback action so that cancelling the dialog reverts the cell to its original value.
            if (params.newValue === 4) {
                const { eventId, marketId } = params.data;
                const oldStatus = params.oldValue;
                setRejectedDialog({
                    open: true,
                    pendingAction: (desc: string) =>
                        handleEdit(eventId, marketId, params.data, changedData, desc),
                    rollbackAction: () => {
                        const api = refInput.current?.getAgGridInstance()?.api;
                        if (api) {
                            api.forEachNode((node: any) => {
                                if (node.data?.eventId === eventId && node.data?.marketId === marketId) {
                                    node.setDataValue('status', oldStatus);
                                }
                            });
                        }
                    },
                });
                return;
            }

            if (params.data.attemptCount >= 3 && params.newValue === 0) { // if change to new and attempt count >= 3, show confirm dialog
                setOpenConfirm(prev => ({
                    ...prev,
                    mode: 'edit',
                    open: true,
                    data: params.data,
                    changedData,
                }));
            } else {
                handleEdit(params.data.eventId, params.data.marketId, params.data, changedData);
            }
        }
    };

    const onCellEditingStarted = (params: any) => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
    };
    const onCellDoubleClicked = (event: any) => {
        const { colId } = event.column; // get column id
        const rowData = event.data;     // get data of the row
        if (colId !== 'status' && rowData.score > 0) {
            let mapping = {
                ...rowData,
                id: rowData.id.split('_')[0], // extract eventId from id field which is in format "eventId_marketId"
                dateTime: rowData.eventDate,
                url: rowData.originalUrl,
                // if rowData.status === 2 => mapped = 1 else -1
                mapped: rowData.status === 2 ? 1 : -1
            };
            setState((prev) => ({ ...prev, m_isOpen: true, m_item: mapping, m_events: [], m_isLoading: true }));
            dataProvider.postData('ListingsMapping', 'FindEvents', mapping)
                .then((response: any) => {
                    if (response.data) {
                        setState((prev) => ({ ...prev, m_events: response.data, m_isLoading: false }));
                    }
                })
                .catch((error: Error) => {
                    notify(`Error: ${error.message}`, { type: 'error' });
                });
        } else if (colId === 'status') {
            event.api.startEditingCell({
                rowIndex: event.rowIndex,
                colKey: 'status'
            });
        }
    };
    //#endregion

    //#region Bulk status update handlers
    const onSelectionChanged = (params: any) => {
        setSelectedRows(params.api.getSelectedRows());
    };

    /**
     * Executes the bulk status update API call for all currently selected rows.
     * Updates grid cells on success and clears selection.
     * @param {number} newStatus - The target status value to apply to all selected rows.
     * @param {string} [description] - Required when newStatus is Rejected (4).
     */
    const executeBulkUpdate = (newStatus: number, description?: string) => {
        setLoading(true);
        const payload = selectedRows.map(r => ({ eventId: r.eventId, marketId: r.marketId, siteId: r.siteId, status: newStatus, description: description ?? null }));
        dataProvider.postData('QueueUpload', 'UpsertQueueUpload', payload)
            .then((res: any) => {
                if (res?.data?.success) {
                    notify(res.data.message, { type: 'success' });
                    const api = refInput.current?.getAgGridInstance()?.api;
                    if (api) {
                        const now = new Date().toISOString();
                        api.forEachNode((node: any) => {
                            if (!node.data) return;
                            const isSelected = selectedRows.some(
                                r => r.eventId === node.data.eventId && r.marketId === node.data.marketId
                            );
                            if (isSelected) {
                                node.setDataValue('status', newStatus);
                                node.setDataValue('lastModified', now);
                                node.setDataValue('updatedBy', res.data.updatedBy);
                                if (newStatus === 0) {
                                    const currentCount = node.data.attemptCount !== undefined && node.data.attemptCount !== null
                                        ? node.data.attemptCount : 1;
                                    node.setDataValue('attemptCount', currentCount + 1);
                                }
                            }
                        });
                        api.deselectAll();
                        setSelectedRows([]);
                    }
                    setBulkStatusOpen(false);
                    setOpenConfirm({ mode: '', open: false, data: {} as RecordData, changedData: {} as ChangeData });
                } else {
                    notify('Bulk update failed', { type: 'error' });
                }
            })
            .catch((err: any) => {
                notify(`Something went wrong: ${err}`, { type: 'error', autoHideDuration: 2000 });
            })
            .finally(() => setLoading(false));
    };

    /**
     * Handles Save in the BulkStatusDialog.
     * - If target status is Rejected (4), closes bulk dialog and opens description popup.
     * - If any selected row has attemptCount >= 3 and target status is 0 (New),
     *   shows a confirmation dialog before proceeding.
     * @param {number} selectedStatus - Status chosen inside BulkStatusDialog.
     */
    const handleBulkStatusSave = (selectedStatus: number) => {
        // Rejected status requires a description — open description popup first.
        if (selectedStatus === 4) {
            setBulkStatusOpen(false);
            setRejectedDialog({
                open: true,
                pendingAction: (desc: string) => executeBulkUpdate(selectedStatus, desc),
                rollbackAction: null, // bulk flow: no individual cell to revert
            });
            return;
        }

        const needsConfirm = selectedStatus === 0 && selectedRows.some(r => (r.attemptCount ?? 1) >= 3);
        if (needsConfirm) {
            // Close bulk dialog and show confirmation before submitting
            setBulkStatusOpen(false);
            setBulkPendingStatus(selectedStatus);
            setOpenConfirm({
                mode: 'bulkConfirm',
                open: true,
                data: {} as RecordData,
                changedData: { newValue: selectedStatus, oldValue: null }
            });
        } else {
            executeBulkUpdate(selectedStatus);
        }
    };
    //#endregion

    /**
     * Handles Save in the RejectedDescriptionDialog.
     * Validates the selected description then executes the stored pending action.
     * @param {string} desc - Description selected by the user.
     */
    const handleSaveRejectedDescription = (desc: string) => {
        if (!desc) {
            notify('A description is required when setting status to Rejected.', { type: 'error' });
            return;
        }
        rejectedDialog.pendingAction!(desc);
        setRejectedDialog({ open: false, pendingAction: null, rollbackAction: null });
    };

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <Title title="Queue Upload List" />

            {loading && <LinearProgress color="primary" sx={{ height: '4px' }} />}

            <FilterListProvider>
                <RenderControls
                    refFilter={refFilter}
                    defaultValues={defaultValues}
                    controls={controls}
                    onSearch={handleSearch}
                    agSource="QueueUpload"
                    agSort={defaultValuesAgGrid.sortField}
                    /* Component A: add/edit/delete rejection descriptions directly from the filter bar */
                    actions={<QueueUploadDescription onDescriptionsChanged={setDescriptions} />}
                />
            </FilterListProvider>

            {/* Bulk action toolbar — visible only when rows are selected */}
            {selectedRows.length > 0 && (
                <BulkActionButtonBar
                    selectedCount={selectedRows.length}
                    onActionClick={() => setBulkStatusOpen(true)}
                />
            )}

            <AgGrid
                ref={refInput}
                list="QueueUpload"
                className="queue-upload-table"
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                defaultValues={defaultValuesAgGrid}
                setDefaultValues={setDefaultValuesAgGrid}
                rowHeight={55}
                rowSelection="multiple"
                editType="fullRow"
                onCellClicked={onCellClicked}
                onCellEditingStopped={onCellEditingStopped}
                onCellEditingStarted={onCellEditingStarted}
                suppressClickEdit={true} // block open editor when click, only open by code to control
                onCellDoubleClicked={onCellDoubleClicked}
                onSelectionChanged={onSelectionChanged}
                tooltipShowDelay={500}
                suppressRowClickSelection={true} // prevent row selection on cell click
                rowMultiSelectWithClick={true} // allow multiple selection with single click (no need to hold Ctrl/Cmd)
                dataAction={() => {
                    // Clear selected rows after data changes
                    if (selectedRows.length > 0) {
                        setSelectedRows([]);
                    }
                }}
            />

            {/* Single-row confirm dialogs */}
            <Confirm
                isOpen={openConfirm.open && openConfirm.mode === 'delete'}
                title="Delete Event?"
                content={`Name: ${openConfirm.data?.name}`}
                onConfirm={() => handleDelete(openConfirm.data.eventId, openConfirm.data.marketId)}
                onClose={() => setOpenConfirm({ mode: '', open: false, data: {} as RecordData, changedData: {} as ChangeData })}
            />
            <Confirm
                isOpen={openConfirm.open && openConfirm.mode === 'edit'}
                title={`Edit Event "${openConfirm.data?.name}" Status?`}
                content={(() => {
                    const oldStatus = queueStatusEnum.find(item => item.id === openConfirm.changedData.oldValue)?.name || openConfirm.changedData.oldValue;
                    const newStatus = queueStatusEnum.find(item => item.id === openConfirm.changedData.newValue)?.name || openConfirm.changedData.newValue;
                    return `Do you want to update [${oldStatus}] => [${newStatus}]?`;
                })()}
                onConfirm={() => handleEdit(openConfirm.data.eventId, openConfirm.data.marketId, openConfirm.data, openConfirm.changedData)}
                onClose={() => setOpenConfirm({ mode: '', open: false, data: {} as RecordData, changedData: {} as ChangeData })}
            />

            {/* Bulk confirm: some selected rows have attemptCount >= 3 and target status is New */}
            <Confirm
                isOpen={openConfirm.open && openConfirm.mode === 'bulkConfirm'}
                title="Confirm Bulk Status Update"
                content={`One or more selected rows have 3 or more attempt counts. Do you still want to update their status to "${queueStatusEnum.find(item => item.id === bulkPendingStatus)?.name ?? bulkPendingStatus}"?`}
                onConfirm={() => executeBulkUpdate(bulkPendingStatus!)}
                onClose={() => {
                    setOpenConfirm({ mode: '', open: false, data: {} as RecordData, changedData: {} as ChangeData });
                    setBulkPendingStatus(null);
                }}
            />

            {/* Bulk status update dialog — extracted to BulkStatusDialog component */}
            <BulkStatusDialog
                open={bulkStatusOpen}
                selectedCount={selectedRows.length}
                loading={loading}
                onClose={() => setBulkStatusOpen(false)}
                onSave={handleBulkStatusSave}
            />

            {/* Rejected description popup — selection only; adding is handled by QueueUploadDescription */}
            <RejectedDescriptionDialog
                open={rejectedDialog.open}
                descriptions={descriptions}
                loading={loading}
                onClose={() => {
                    // For single-row edits, revert the AgGrid cell status to the value before the user picked Rejected.
                    rejectedDialog.rollbackAction?.();
                    setRejectedDialog({ open: false, pendingAction: null, rollbackAction: null });
                }}
                onSave={handleSaveRejectedDescription}
            />

            <MappingPopup dataProvider={dataProvider} data={state} smMarket={state.selectMarkektId} handleClose={handleCloseDialog}/>
        </div>
    );
};

export default QueueUploadView;
